import queryString from "qs";
import { instance } from "api/apiBase";
import type { PostShowingSurveyInsights } from "api/post-showing-survey/types";
import type {
  LeadJourneyResponse,
  LeasingFunnelResponse,
  ShowingsCompletedOverTimeResponse
} from "api/crm-analytics";
import { LeadType } from "views/leads-id/types";

export type ModuleKind =
  | "boom_report_total_enrollments"
  | "boom_report_enrolled"
  | "boom_report_unenrolled"
  | "boom_report_daily_enrollments"
  | "boom_report_pending_enrollments"
  | "crm_leads_created"
  | "crm_leads_by_source"
  | "crm_days_on_market"
  | "crm_days_to_decision"
  | "crm_listing_views"
  | "crm_time_to_manual_contact"
  | "crm_touch_points_per_lead"
  | "crm_showings_scheduled"
  | "crm_showings_completed"
  | "crm_showings_no_shows"
  | "crm_approvals_by_source"
  | "crm_lead_pipeline"
  | "crm_application_pipeline"
  | "crm_prescreening_criteria_failures"
  | "boom_screen_applications_submitted"
  | "screen_submissions_decisions_weekly"
  | "boom_screen_time_to_applicant_submission"
  | "boom_screen_time_to_decision"
  | "boom_screen_time_to_decision_weekly"
  | "boom_screen_time_to_submission"
  | "crm_post_showing_survey"
  | "crm_leasing_funnel"
  | "crm_lead_journey"
  | "crm_showings_completed_over_time";

export type ModuleDisplay =
  | "number"
  | "series_line"
  | "series_bar"
  | "pie_chart"
  | "avg_median"
  | "bar_chart"
  | "bar_chart_horizontal"
  | "line_chart"
  | "table"
  | "funnel"
  | "survey_insights"
  | "leasing_funnel"
  | "lead_journey"
  | "showings_over_time";

const POINT_IN_TIME_KINDS: ModuleKind[] = [
  "boom_report_total_enrollments",
  "boom_report_pending_enrollments",
  "crm_days_on_market"
];

export const isPointInTime = (kind: ModuleKind): boolean =>
  POINT_IN_TIME_KINDS.includes(kind);

export type RangePreset =
  | "last_7_days"
  | "last_30_days"
  | "last_60_days"
  | "last_90_days"
  | "last_12_months"
  | "all_time"
  | "custom";

export type UnitFilterPreset = "all" | "available_only" | "custom_unit_ids";

interface Dashboard {
  id: number;
  name: string;
  range_preset: RangePreset;
  start: string | null;
  finish: string | null;
  unit_filter_preset: UnitFilterPreset;
  unit_ids: number[];
  property_ids: number[];
  property_group_ids: number[];
  owner_report: boolean;
  default_owner_report: boolean;
  created_at: string;
  updated_at: string;
}

export interface ModulePosition {
  id: number;
  kind: ModuleKind;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardDetail extends Dashboard {
  modules: ModulePosition[];
}

export interface ModuleSeries {
  key: string;
  label: string;
  color: string | null;
  values: number[];
}

export interface ModuleSlice {
  key: string;
  label: string;
  color: string | null;
  value: number;
}

export interface FunnelTransition {
  // Lead-pipeline transitions are keyed by stage_id; application-pipeline
  // transitions are keyed by status. Cards branch on whichever is present.
  stage_id?: string | null;
  status?: string | null;
  stage_name: string;
  count: number;
}

export interface FunnelStageSource {
  source_id: number | null;
  source_name: string | null;
  color: string | null;
  count: number;
  percentage: number;
}

export interface FunnelStage {
  // See FunnelTransition: stage_id (lead) vs status (application).
  stage_id?: string;
  status?: string;
  stage_name: string;
  total_count: number;
  transitions: FunnelTransition[];
  by_source?: FunnelStageSource[];
}

export interface ModuleFunnelData {
  stages: FunnelStage[];
  truncated: boolean;
}

export type ModuleData =
  | { analytics_count: number }
  | { categories: string[]; series: ModuleSeries[] }
  | { slices: ModuleSlice[] }
  | { average: number | null; median: number | null }
  | ModuleFunnelData
  | PostShowingSurveyInsights
  // The Leasing funnel and Lead journey serve their own shapes rather than one
  // of the generic ones; `steps` and `milestones` discriminate them (PLT-680).
  | LeasingFunnelResponse
  | LeadJourneyResponse
  | ShowingsCompletedOverTimeResponse;

interface DashboardModule {
  id: number;
  dashboard_id: number;
  kind: ModuleKind;
  display_type: ModuleDisplay;
  data: ModuleData;
  created_at: string;
  updated_at: string;
}

interface ModuleDataResponse {
  kind: ModuleKind;
  data: ModuleData;
}

const BASE_URL = "/portal/v1/analytics/dashboards";

export const analyticsDashboardsApi = {
  list: async (params?: { owner_report?: boolean }): Promise<Dashboard[]> => {
    const { data } = await instance.get<Dashboard[]>(BASE_URL, { params });
    return data;
  },

  show: async (id: number): Promise<DashboardDetail> => {
    const { data } = await instance.get<DashboardDetail>(`${BASE_URL}/${id}`);
    return data;
  },

  create: async (params: {
    name: string;
    range_preset?: RangePreset;
    start?: string;
    finish?: string;
    unit_filter_preset?: UnitFilterPreset;
    unit_ids?: number[];
    property_ids?: number[];
    property_group_ids?: number[];
  }): Promise<DashboardDetail> => {
    const { data } = await instance.post<DashboardDetail>(BASE_URL, params);
    return data;
  },

  update: async (
    id: number,
    params: {
      name?: string;
      range_preset?: RangePreset;
      start?: string;
      finish?: string;
      unit_filter_preset?: UnitFilterPreset;
      unit_ids?: number[];
      property_ids?: number[];
      property_group_ids?: number[];
    }
  ): Promise<DashboardDetail> => {
    const { data } = await instance.patch<DashboardDetail>(
      `${BASE_URL}/${id}`,
      params
    );
    return data;
  },

  destroy: async (id: number): Promise<void> => {
    await instance.delete(`${BASE_URL}/${id}`);
  },

  createModule: async (
    dashboardId: number,
    params: {
      kind: ModuleKind;
      display: ModuleDisplay;
      x: number;
      y: number;
      w: number;
      h: number;
    }
  ): Promise<DashboardModule> => {
    const { data } = await instance.post<DashboardModule>(
      `${BASE_URL}/${dashboardId}/modules`,
      {
        kind: params.kind,
        display_type: params.display,
        x: params.x,
        y: params.y,
        w: params.w,
        h: params.h
      }
    );
    return data;
  },

  updateModule: async (
    moduleId: number,
    params: { x: number; y: number; w: number; h: number }
  ): Promise<DashboardModule> => {
    const { data } = await instance.patch<DashboardModule>(
      `${BASE_URL}/modules`,
      params,
      { params: { id: moduleId } }
    );
    return data;
  },

  showModule: async (
    moduleId: number,
    range?: {
      start?: string;
      finish?: string;
      allTime?: boolean;
      timezone?: string;
      property_ids?: number[];
      filters?: Record<string, unknown>;
    }
  ): Promise<DashboardModule> => {
    const { data } = await instance.get<DashboardModule>(
      `${BASE_URL}/modules/data`,
      {
        params: {
          id: moduleId,
          ...(range?.start ? { start: range.start } : {}),
          ...(range?.finish ? { finish: range.finish } : {}),
          ...(range?.allTime ? { all_time: true } : {}),
          ...(range?.timezone ? { timezone: range.timezone } : {}),
          ...(range?.property_ids?.length
            ? { property_ids: range.property_ids }
            : {}),
          ...(range?.filters ? { filters: range.filters } : {})
        },
        paramsSerializer: (params) =>
          queryString.stringify(params, { arrayFormat: "brackets" })
      }
    );
    return data;
  },

  destroyModule: async (
    dashboardId: number,
    moduleId: number
  ): Promise<void> => {
    await instance.delete(`${BASE_URL}/${dashboardId}/modules/${moduleId}`);
  },

  getModuleData: async (moduleId: number): Promise<ModuleDataResponse> => {
    const { data } = await instance.get<ModuleDataResponse>(
      `${BASE_URL}/modules/data`,
      { params: { id: moduleId } }
    );
    return data;
  },

  getDataByKind: async (
    kind: ModuleKind,
    display_type: ModuleDisplay,
    range?: {
      start?: string;
      finish?: string;
      timezone?: string;
      unit_ids?: number[];
      property_ids?: number[];
      property_group_ids?: number[];
      filters?: Record<string, unknown>;
    }
  ): Promise<DashboardModule> => {
    const { data } = await instance.get<DashboardModule>(
      `${BASE_URL}/modules/data`,
      {
        params: { kind, display_type, ...range },
        paramsSerializer: (params) =>
          queryString.stringify(params, { arrayFormat: "brackets" })
      }
    );
    return data;
  },

  getModuleRows: async <T = ModuleRowItem>(params: {
    kind?: ModuleKind;
    module_id?: number;
    display_type?: ModuleDisplay;
    start?: string;
    finish?: string;
    timezone?: string;
    unit_filter_preset?: UnitFilterPreset;
    unit_ids?: number[];
    property_ids?: number[];
    property_group_ids?: number[];
    page: number;
    per_page: number;
  }): Promise<ModuleRowsResponse<T>> => {
    const { data } = await instance.get<ModuleRowsResponse<T>>(
      `${BASE_URL}/modules/data/rows`,
      { params }
    );
    return data;
  },

  getTimeToManualContactRows: async (params: {
    start?: string;
    finish?: string;
    timezone?: string;
    unit_filter_preset?: UnitFilterPreset;
    unit_ids?: number[];
    property_ids?: number[];
    property_group_ids?: number[];
    lead_type?: LeadType;
  }): Promise<TimeToManualContactRowsResponse> => {
    const { data } = await instance.get<TimeToManualContactRowsResponse>(
      `${BASE_URL}/modules/data/rows`,
      { params: { kind: "crm_time_to_manual_contact", ...params } }
    );
    return data;
  },

  getTouchPointsPerLeadRows: async (params: {
    start?: string;
    finish?: string;
    timezone?: string;
    unit_filter_preset?: UnitFilterPreset;
    unit_ids?: number[];
    property_ids?: number[];
    property_group_ids?: number[];
    lead_type?: LeadType;
  }): Promise<TouchPointsPerLeadRowsResponse> => {
    const { data } = await instance.get<TouchPointsPerLeadRowsResponse>(
      `${BASE_URL}/modules/data/rows`,
      { params: { kind: "crm_touch_points_per_lead", ...params } }
    );
    return data;
  }
};

export interface TimeToManualContactRowItem {
  lead_id: number;
  full_name: string | null;
  email: string | null;
  unit_label: string | null;
  duration_seconds: number;
}

interface TimeToManualContactRowGroup {
  key: number;
  label: string;
  items: TimeToManualContactRowItem[];
}

interface TimeToManualContactRowsResponse {
  groups: TimeToManualContactRowGroup[];
  total: number;
}

export type TouchPointsPerLeadRowItem = Pick<
  TimeToManualContactRowItem,
  "lead_id" | "full_name" | "email" | "unit_label"
> & { touch_points: number };

interface TouchPointsPerLeadRowGroup {
  key: number;
  label: string;
  items: TouchPointsPerLeadRowItem[];
}

interface TouchPointsPerLeadRowsResponse {
  groups: TouchPointsPerLeadRowGroup[];
  total: number;
}

export interface ModuleRowItem {
  id: number;
  customer_id?: number;
  name: string;
  email?: string;
  unit?: string;
  property_name?: string;
  property_address?: string;
  date: string;
}

// Superset of the per-kind CRM drill-down row shapes served by data/rows
// (leads_created, days_on_market, days_to_decision, showings_*). All fields
// are optional so a single dashboard modal can render every CRM kind's rows.
export interface CrmDashboardRow {
  id?: number;
  lead_id?: number | null;
  full_name?: string | null;
  lead_first_name?: string | null;
  lead_last_name?: string | null;
  email?: string | null;
  source_name?: string | null;
  unit_label?: string | null;
  unit_title?: string | null;
  property_id?: number | null;
  property_name?: string | null;
  property_address?: string | null;
  partner_user_name?: string | null;
  days_on_market?: number | null;
  days?: number;
  decision?: string;
  applicant_names?: string;
  available_for_application?: boolean;
  created_at?: string;
  transitioned_at?: string;
}

interface ModuleRowGroup<T = ModuleRowItem> {
  key?: string;
  label?: string;
  sort_order?: number;
  items: T[];
}

interface ModuleRowsResponse<T = ModuleRowItem> {
  kind: ModuleKind;
  groups: ModuleRowGroup<T>[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
  };
}
